import { IconProps, IconSquare, IconSquareCheckFilled } from '@tabler/icons-react'
import { useState } from 'react'
import { useLocation } from 'react-router'

import { pages } from '~/app/routes'
import { AstraChip, AstraDropdownItem, AstraDropdownMenu, AstraHoverCard, AstraMultiSelect, AstraParagraph, MenuItemAction, SelectOption } from '~/astra'
import { useResolveSelectedSpecialties } from '~/hooks/useResolveSelectedSpecialties'
import { SelectedSpecialties } from '~/store/slices/filterSlice'
import { useStore } from '~/store/store'
import { getSpecialityName, useAssignableSpecialities } from '~/storm/common/specialty'
import { trackUserEvent } from '~/utils/analytics'

import { SquareLetterIcon } from './SquareLetterIcon'

const PLACEHOLDER = 'Fagområder'
const ALL_VALUE = 'all'

// Match the checked color of the standard AstraCheckbox (bg-brand-strong-selected).
const CheckedIcon = (props: IconProps) => <IconSquareCheckFilled {...props} className={`${props.className ?? ''} text-brand-emphasis`} size={'large'} />

const SpecialtyIcon = ({ letter }: { letter: string }) => (
    <div className="text-strong flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-md bg-surface-base">
        <SquareLetterIcon letter={letter} size={20} stroke={1.5} />
    </div>
)

export const SpecialtySelect = () => {
    const { pathname } = useLocation()
    const specialities = useAssignableSpecialities()
    const selectedSpecialties = useStore(state => state.appFilters.selectedSpecialties)
    const { setSelectedSpecialties } = useStore(state => state.appFilters.actions)
    const isNavSidebarOpen = useStore(state => state.app.isNavSidebarOpen)
    const [isPointerOver, setIsPointerOver] = useState(false)
    const [isMenuOpen, setIsMenuOpen] = useState(false)

    useResolveSelectedSpecialties()

    const usesDepartmentFilter = !pages.DOWNLOAD.views.download.path.startsWith(pathname)

    const options: SelectOption<string>[] = [
        { value: ALL_VALUE, label: 'Alle' },
        ...specialities.map(specialty => ({ value: specialty.uuid, label: getSpecialityName(specialty) })).sort((a, b) => a.label.localeCompare(b.label)),
    ]

    const values = selectedSpecialties === 'all' ? [ALL_VALUE] : selectedSpecialties
    const selectedOptions = options.filter(option => values.includes(option.value))
    const firstLetter = selectedOptions[0]?.label.at(0) ?? ''

    const handleChange = (selected: SelectOption<string>[]) => {
        const nextValues = selected.map(option => option.value)
        const specifics = nextValues.filter(value => value !== ALL_VALUE)
        // Choosing "Alle", or clearing the selection, resolves to all specialties.
        const justPickedAll = selectedSpecialties !== 'all' && nextValues.includes(ALL_VALUE)
        const next: SelectedSpecialties = justPickedAll || specifics.length === 0 ? 'all' : specifics

        void trackUserEvent('SPECIALTIES_SELECTED', {
            specialtyNames: next === 'all' ? ['Alle'] : selected.filter(option => option.value !== ALL_VALUE).map(option => option.label),
        })
        setSelectedSpecialties(next)
    }

    if (!usesDepartmentFilter || !specialities.length) {
        return null
    }

    const toggleOption = (option: SelectOption<string>) => {
        const next = values.includes(option.value) ? selectedOptions.filter(selected => selected.value !== option.value) : [...selectedOptions, option]
        handleChange(next)
    }

    const dropdownItems: MenuItemAction[] = options.map(option => ({
        type: 'action',
        title: option.label,
        isActive: values.includes(option.value),
        icon: values.includes(option.value) ? CheckedIcon : IconSquare,
        // Keep the menu open so several specialties can be toggled in one go.
        action: event => {
            event.preventDefault()
            toggleOption(option)
        },
    }))

    return (
        <AstraHoverCard
            placement="right"
            open={isPointerOver && !isMenuOpen}
            onOpenChange={open => {
                if (!open) setIsPointerOver(false)
            }}
            noPadding
            hoverContent={
                <div className="flex max-w-xs flex-col">
                    <div className="p-3">
                        <AstraParagraph>{PLACEHOLDER}</AstraParagraph>
                    </div>
                    <hr />
                    <div className="p-3">
                        {selectedOptions.length ? (
                            <div className="flex flex-col flex-wrap gap-1">
                                {selectedOptions.map(option => (
                                    <AstraChip key={option.value} label={option.label} />
                                ))}
                            </div>
                        ) : (
                            <AstraParagraph variant="body-small" className="text-subtle">
                                Ingen valgt
                            </AstraParagraph>
                        )}
                    </div>
                </div>
            }
        >
            <div
                className="flex w-full items-center gap-2 overflow-hidden px-1.5"
                onMouseEnter={() => setIsPointerOver(true)}
                onMouseLeave={() => setIsPointerOver(false)}
            >
                {isNavSidebarOpen ? (
                    <SpecialtyIcon letter={firstLetter} />
                ) : (
                    <AstraDropdownMenu
                        placement="right-start"
                        sideOffset={5}
                        onOpenChange={setIsMenuOpen}
                        dropdownContent={
                            <>
                                <div className="px-2 py-1">
                                    <AstraParagraph variant="body-small" weight="medium" className="text-subtle">
                                        {PLACEHOLDER}
                                    </AstraParagraph>
                                </div>
                                {dropdownItems.map(item => (
                                    <AstraDropdownItem key={item.title} option={item} />
                                ))}
                            </>
                        }
                    >
                        <button aria-label={PLACEHOLDER} data-test="nav-specialty-selector-collapsed">
                            <SpecialtyIcon letter={firstLetter} />
                        </button>
                    </AstraDropdownMenu>
                )}
                <div className="min-w-0 flex-1">
                    <AstraMultiSelect
                        className="max-h-8 w-full min-w-[172px]"
                        displayMode="inline"
                        displaySelectedAs="text"
                        menuTitle={PLACEHOLDER}
                        options={options}
                        values={values}
                        isClearable={false}
                        onChange={handleChange}
                        onMenuOpenChange={setIsMenuOpen}
                        placeholder={PLACEHOLDER}
                        data-test="nav-specialty-selector"
                    />
                </div>
            </div>
        </AstraHoverCard>
    )
}
